import { useGetPurchaseRequestListQuery } from '@/pages/mortgage/apis/purchaseAPI';
import { formatDate } from '@/utils/commonUtils';
import { FormOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Row,
  Col,
  Typography,
  Table,
  theme,
  Tag,
  Grid,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { purchaseFormRoutes } from '../constant/stepperRoutes';
import Meeting from '@/components/Meeting/Meeting';
import CalculatorListComponent from '@/components/Calculator/Calculator';
import useScrollToTop from '@/hooks/useScrollTop';
import TableTitle from '@/components/Elements/Heading/TableTitle';
import React, { useEffect, useState } from 'react';
import { useTheme } from '@/contexts/themeContext';
import { useAppDispatch } from '@/hooks/hooks';
import { resetIncomeFormField } from '@/store/slices/purchase/purchaseSlice';
import {
  useFetchUserDataQuery,
  useFetchUserProfileQuery,
} from '@/store/apis/userApi';

const { useBreakpoint } = Grid;

const { useToken } = theme;

const Purchase = () => {
  const { sidebarCollapseState } = useTheme();

  const { md } = useBreakpoint();
  const navigate = useNavigate();
  useScrollToTop();
  const { token } = useToken();
  const [meetingLink, setMeetingLink] = useState('');
  const { data: purchaseRequestList, isLoading: purchaseRequestListLoading } =
    useGetPurchaseRequestListQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const startPurchaseAssessment = () => {
    navigate('step/primary-detail');
  };

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 5,
  });
  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const columns: any = [
    // {
    //   title: <TableTitle>S.N</TableTitle>,
    //   key: 'index',
    //   align: 'center',
    //   render: (_: any, __: any, index: number) => {
    //     const page = pagination.current;
    //     const pageSize = pagination.pageSize;
    //     const serialNumber = (page - 1) * pageSize + index + 1;
    //     return (
    //       <Typography.Text
    //         style={{
    //           fontWeight: '600',
    //         }}
    //       >
    //         {serialNumber}.{' '}
    //       </Typography.Text>
    //     );
    //   },
    //   width: 100,
    // },
    {
      title: <TableTitle>Deal ID</TableTitle>,
      dataIndex: 'deal_id',
      key: 'deal_id',
      align: 'center',
      render: (text: string) => (
        <Typography.Text>{text || 'N/A'}</Typography.Text>
      ),
      width: 200,
    },
    {
      title: <TableTitle>Current Stage</TableTitle>,
      align: 'center',
      dataIndex: 'current_step',
      key: 'current_step',

      render: (_: any, record: any) => {
        const color =
          record.status === 'in_progress' || record.status === 'draft'
            ? 'orange'
            : 'green';
        const text =
          record.status === 'in_progress' || record.status === 'draft'
            ? record.current_step
                .split('_')
                .map((word: string) => word.toUpperCase())
                .join(' ')
            : 'ONLINE ASSESSMENT SUBMITTED';
        return <Tag color={color}>{text}</Tag>;
      },
      width: 200,
    },
    {
      title: <TableTitle>Status</TableTitle>,
      align: 'center',
      dataIndex: 'status',
      key: 'status',
      render: (
        _: any,
        { status, status_display }: { status: string; status_display: string },
      ) => {
        const color =
          status === 'in_progress'
            ? 'orange'
            : status === 'draft'
              ? 'blue'
              : 'green';
        return (
          <Tag color={color}>
            {status
              .split('_')
              .map((word: string) => word.toUpperCase())
              .join(' ')}
          </Tag>
        );
      },
      width: 200,
    },
    {
      title: <TableTitle>Created Date</TableTitle>,
      align: 'center',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_: any, { created_at }: { created_at: string | null }) => {
        if (!created_at) return null;
        return (
          <Typography.Paragraph>{formatDate(created_at)}</Typography.Paragraph>
        );
      },
      width: 300,
    },
    {
      title: <TableTitle>Action</TableTitle>,
      align: 'center',
      key: 'action',
      render: (_: any, record: any) => {
        return (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            {(record.status === 'in_progress' || record.status === 'draft') && (
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  if (record.status === 'in_progress') {
                    navigate(
                      `${purchaseFormRoutes[record.current_step]}/${record.id}`,
                    );
                  } else if (
                    record.status === 'draft' &&
                    record.current_step === 'income_detail'
                  ) {
                    navigate(
                      `${purchaseFormRoutes[record.current_step]}/${record.id}/update`,
                    );
                  } else {
                    navigate(
                      `${purchaseFormRoutes[record.current_step]}/${record.id}`,
                    );
                  }
                }}
              >
                Continue
              </Button>
            )}
            <Button
              size="small"
              type="primary"
              onClick={() => {
                window.open(meetingLink, '_blank');
              }}
            >
              Book a Meeting
            </Button>
            {record.status === 'submitted' && (
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  navigate(`summary/${record.id}`);
                }}
              >
                View Summary
              </Button>
            )}
          </div>
        );
      },
      width: 300,
    },
  ];

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(resetIncomeFormField());
  }, []);

  const { data: userProfileData, refetch } = useFetchUserDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const userProfile = userProfileData?.data;

  useEffect(() => {
    if (!userProfile) return;
    const params = new URLSearchParams({
      embed: 'true',
      ...(userProfile?.firstName && { firstname: userProfile?.firstName }),
      ...(userProfile?.lastName && { lastname: userProfile?.lastName }),
      ...(userProfile?.email && { email: userProfile?.email }),
      ...(userProfile?.phone && { phone: userProfile?.phone }),
    });
    setMeetingLink(
      `https://meetings.hubspot.com/odin-mortgage/strategy-meeting-purchase?${params}`,
    );
  }, [userProfile]);

  return (
    <div style={{ width: '100%', marginTop: '20px' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  justifyContent: md ? 'space-between' : 'center',
                  alignItems: md ? 'center' : 'stretch',
                  flexDirection: md ? 'row' : 'column',
                  gap: md ? 0 : 12,
                }}
              >
                <Typography.Title
                  level={4}
                  style={{
                    margin: 0,
                    marginTop: md ? 0 : 12,
                    textAlign: md ? 'left' : 'center',
                  }}
                >
                  Purchase Applications
                </Typography.Title>

                <Button
                  type="primary"
                  style={{
                    width: md ? 'fit-content' : '100%',
                  }}
                  onClick={startPurchaseAssessment}
                >
                  Start Purchase Application
                </Button>
              </div>
            }
          >
            <Table
              style={{ width: '100%' }}
              bordered
              dataSource={purchaseRequestList?.data}
              columns={columns}
              rowKey="id"
              loading={purchaseRequestListLoading}
              pagination={{
                pageSize: pagination.pageSize,
                current: pagination.current,
              }}
              onChange={handleTableChange}
              scroll={{
                x: 'max-content',
              }}
            />
          </Card>
        </Col>
        <Col xs={24} md={24} xl={12}>
          <Meeting meetingUrl={meetingLink} title="Book a Meeting" />
        </Col>
        <Col xs={24} md={24} xl={12}>
          <CalculatorListComponent title="Mortgage Calculators" />
        </Col>
      </Row>
    </div>
  );
};

export default Purchase;
